"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, MessageSquare, User, MapPin, Package, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";
import {
  useGetTestimonialsQuery,
  useCreateTestimonialMutation,
} from "@/store";
import { toast } from "sonner";
import { TablePagination } from "@/components/admin";

const DEFAULT_PAGE_SIZE = 12;

export default function TestimonialsScreen() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [ratingFilter, setRatingFilter] = useState<"ALL" | number>("ALL");
  const [typeFilter, setTypeFilter] = useState<"ALL" | "GENERAL" | "PRODUCT">("ALL");

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 5,
    comment: "",
    avatarUrl: "",
    location: "",
    productId: "",
  });

  const filters = {
    page,
    limit,
    rating: ratingFilter !== "ALL" ? ratingFilter : undefined,
    isGeneral: typeFilter === "GENERAL" ? true : typeFilter === "PRODUCT" ? false : undefined,
  };

  const { data: testimonialsData, isLoading } = useGetTestimonialsQuery(filters);
  const [createTestimonial, { isLoading: isSubmitting }] = useCreateTestimonialMutation();

  const testimonials = testimonialsData?.data || [];
  const pagination = testimonialsData?.pagination || {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 0,
  };

  useEffect(() => {
    setPage(1);
  }, [ratingFilter, typeFilter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.comment) {
      toast.error("Name and comment are required");
      return;
    }

    try {
      await createTestimonial({
        name: formData.name,
        email: formData.email || undefined,
        rating: formData.rating,
        comment: formData.comment,
        avatarUrl: formData.avatarUrl || undefined,
        location: formData.location || undefined,
        productId: formData.productId || undefined,
      }).unwrap();

      toast.success("Thank you! Your testimonial has been submitted and is pending approval.");
      setFormData({
        name: "",
        email: "",
        rating: 5,
        comment: "",
        avatarUrl: "",
        location: "",
        productId: "",
      });
      setShowForm(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error || error?.data?.message || "Failed to submit testimonial";
      toast.error(errorMessage);
    }
  };

  const renderStars = (rating: number, size: "sm" | "md" = "md") => {
    const starSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <SEO
        title="Customer Testimonials | Zest and Zeal"
        description="Read what our customers have to say about Zest and Zeal. Real reviews and testimonials from satisfied customers."
      />
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Customer Testimonials</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See what our customers have to say about their experience with Zest and Zeal
            </p>
          </motion.div>
        </div>

        {/* Submit Testimonial Button */}
        <div className="flex justify-center mb-8">
          <Button onClick={() => setShowForm(!showForm)} size="lg" className="gap-2">
            <MessageSquare className="h-5 w-5" />
            {showForm ? "Hide Form" : "Share Your Experience"}
          </Button>
        </div>

        {/* Submit Form */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-12"
          >
            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rating">Rating *</Label>
                    <Select
                      value={String(formData.rating)}
                      onValueChange={(value) =>
                        setFormData({ ...formData, rating: parseInt(value) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Stars - Excellent</SelectItem>
                        <SelectItem value="4">4 Stars - Very Good</SelectItem>
                        <SelectItem value="3">3 Stars - Good</SelectItem>
                        <SelectItem value="2">2 Stars - Fair</SelectItem>
                        <SelectItem value="1">1 Star - Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="comment">Your Review *</Label>
                    <Textarea
                      id="comment"
                      value={formData.comment}
                      onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                      placeholder="Share your experience with Zest and Zeal..."
                      rows={5}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="City, Country"
                      />
                    </div>
                    <div>
                      <Label htmlFor="productId">Product ID (optional - for product review)</Label>
                      <Input
                        id="productId"
                        value={formData.productId}
                        onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                        placeholder="Leave empty for general testimonial"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isSubmitting} className="gap-2">
                      <Send className="h-4 w-4" />
                      {isSubmitting ? "Submitting..." : "Submit Testimonial"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1">
            <Label>Filter by Rating</Label>
            <Select
              value={ratingFilter === "ALL" ? "ALL" : String(ratingFilter)}
              onValueChange={(value) =>
                setRatingFilter(value === "ALL" ? "ALL" : parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Ratings</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label>Filter by Type</Label>
            <Select
              value={typeFilter}
              onValueChange={(value) => setTypeFilter(value as "ALL" | "GENERAL" | "PRODUCT")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                <SelectItem value="GENERAL">General Testimonials</SelectItem>
                <SelectItem value="PRODUCT">Product Reviews</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No testimonials yet</h3>
            <p className="text-muted-foreground mb-4">
              Be the first to share your experience!
            </p>
            <Button onClick={() => setShowForm(true)}>Share Your Experience</Button>
          </div>
        ) : (
          <>
            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full">
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {/* Author Info */}
                        <div className="flex items-start gap-3">
                          {testimonial.avatarUrl ? (
                            <img
                              src={testimonial.avatarUrl}
                              alt={testimonial.name || "User"}
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">
                              {testimonial.name || testimonial.user?.name || "Anonymous"}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              {testimonial.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  <span>{testimonial.location}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Rating */}
                        <div>{renderStars(testimonial.rating)}</div>

                        {/* Comment */}
                        <p className="text-sm leading-relaxed">{testimonial.comment}</p>

                        {/* Product Badge */}
                        {testimonial.productId && testimonial.product && (
                          <Badge variant="outline" className="gap-1">
                            <Package className="h-3 w-3" />
                            {testimonial.product.name}
                          </Badge>
                        )}

                        {/* Date */}
                       {/* <p className="text-xs text-muted-foreground">
                          {new Date(testimonial.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p> */}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <TablePagination
                pagination={pagination}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
